import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import {
  accountEmail,
  currentMergeEpoch,
  deleteReminder,
  fetchReminders,
  isLoggedIn,
  mergePending,
  pageUrl,
  saveReminder,
  signInUrl,
} from '@theme/wishlist';
import { normalizePhone } from '@theme/consent';

const CELL = '[data-wishlist-item]';
const REMIND_PARAM = 'remind';

/** Read once per page: the merge can re-render the grid, so no single instance owns it. */
let pendingResume = (() => {
  const url = new URL(window.location.href);
  const key = url.searchParams.get(REMIND_PARAM);
  if (key === null) return null;

  url.searchParams.delete(REMIND_PARAM);
  history.replaceState(history.state, '', url.pathname + url.search + url.hash);
  return /^\d+:\d*$/.test(key) ? key : null;
})();
let searchedLastPage = false;

/** datetime-local wants `YYYY-MM-DDTHH:mm` in LOCAL time, which toISOString cannot give. */
function localInputValue(date) {
  const pad = (value) => String(value).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

class WishlistReminders extends Component {
  #abort = null;
  #editing = null;
  #byKey = new Map();
  #consented = false;
  #epoch = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#epoch = currentMergeEpoch();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.#dialog?.addEventListener('close', this.#onCancelled, { signal });
    this.#signInDialog?.addEventListener('close', this.#onCancelled, { signal });

    this.dataset.enabled = String(isLoggedIn() || !!this.#signInDialog);
    this.dataset.guest = String(!isLoggedIn());
    if (isLoggedIn()) {
      // Hold the buttons until state loads, or an item with a reminder reads "Set reminder".
      this.#setPending(true);
      void this.#hydrate();
    }
  }

  #setPending(pending) {
    for (const toggle of this.querySelectorAll('[data-reminder-edit]')) {
      // setAttribute, not toggleAttribute: the latter writes aria-disabled="", not a valid token.
      toggle.setAttribute('aria-disabled', String(pending));
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  #say(message) {
    const region = this.querySelector('[data-reminder-live]');
    if (!region || !message) return;

    // Cleared first: a region re-set to the text it already holds is not re-announced.
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = message;
    });
  }

  async #hydrate() {
    try {
      const { reminders, consent } = await fetchReminders();
      if (!this.isConnected) return;

      this.#consented = consent?.granted === true;
      this.#byKey = new Map((reminders ?? []).map((row) => [row.item_key, row]));
      for (const cell of this.querySelectorAll(CELL)) {
        this.#render(cell, this.#byKey.get(cell.dataset.wishlistItem));
      }
      this.#setPending(false);
      this.#resumeFromSignIn();
    } catch {
      // Deliberately NOT re-enabled: a click would open the dialog blind and save a duplicate.
      const message = this.dataset.unavailableText;
      for (const toggle of this.querySelectorAll('[data-reminder-edit]')) {
        toggle.title = message ?? '';
      }
      this.#say(message);
    }
  }

  #resumeFromSignIn() {
    if (!pendingResume) return;
    if (!mergePending()) {
      this.#openPending();
      return;
    }

    document.addEventListener(
      ThemeEvents.wishlistUpdate,
      (event) => {
        const detail = event.detail ?? {};
        if (!('merged' in detail) && !('mergeError' in detail)) return;
        // Added items reload the panel; the replacement instance resumes instead.
        if (detail.merged > 0) return;
        this.#openPending();
      },
      { signal: this.#abort?.signal }
    );
  }

  #openPending() {
    const key = pendingResume;
    if (!key || !this.isConnected || this.#epoch < currentMergeEpoch()) return;

    const cell = [...this.querySelectorAll(CELL)].find((node) => node.dataset.wishlistItem === key);
    if (!cell && !searchedLastPage) {
      searchedLastPage = true;
      const loading = !document.dispatchEvent(new CustomEvent(ThemeEvents.wishlistShowLastPage, { cancelable: true }));
      if (loading) return;
    }
    pendingResume = null;

    const toggle = cell?.querySelector('[data-reminder-edit]');
    if (!toggle || toggle.hidden) return;
    this.#open(cell);
  }

  #format(dueAt) {
    const date = new Date(dueAt);
    if (!Number.isFinite(date.getTime())) return '';
    try {
      return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
        date
      );
    } catch {
      return date.toLocaleString();
    }
  }

  #render(cell, reminder) {
    const toggle = cell.querySelector('[data-reminder-edit]');
    const label = toggle?.querySelector('[data-reminder-label]');
    const when = toggle?.querySelector('[data-reminder-when]');
    if (!toggle) return;

    // textContent, never innerHTML, everywhere a value reaches the page.
    if (when) {
      when.textContent = reminder ? this.#format(reminder.due_at) : '';
      when.hidden = !reminder;
    }

    if (label) label.textContent = reminder ? toggle.dataset.shortEdit : toggle.dataset.shortAdd;
    toggle.setAttribute('aria-label', reminder ? toggle.dataset.labelEdit : toggle.dataset.labelAdd);

    // A reminder set while in stock still fires, so it stays reachable to cancel.
    if (toggle.hasAttribute('data-reminder-soldout')) toggle.hidden = !reminder;
  }

  #onClick = (event) => {
    const cancel = event.target.closest('[data-reminder-cancel]');
    if (cancel && this.contains(cancel)) {
      event.preventDefault();
      this.#close();
      return;
    }

    const remove = event.target.closest('[data-reminder-remove]');
    if (remove && this.contains(remove)) {
      event.preventDefault();
      void this.#remove(this.#editing);
      return;
    }

    const toggle = event.target.closest('[data-reminder-edit]');
    if (!toggle || !this.contains(toggle)) return;

    event.preventDefault();
    if (this.dataset.enabled !== 'true') return;
    if (this.#isGuest) {
      this.#openSignIn(toggle.closest(CELL));
      return;
    }
    // Tests the VALUE, not presence: the attribute is always written, as "true" or "false".
    if (toggle.getAttribute('aria-disabled') === 'true') return;
    this.#open(toggle.closest(CELL));
  };

  #pointerPreset = false;
  #saving = false;

  get #isGuest() {
    return this.dataset.guest === 'true';
  }

  #onPointerDown = (event) => {
    // Match the whole label: arrow-keying the radios fires change with no preceding pointerdown.
    this.#pointerPreset = !!event.target.closest('.ca-wishlist__preset');
  };

  #onChange = (event) => {
    if (!event.target.closest('[data-reminder-preset], [data-reminder-field], [data-reminder-email], [data-reminder-mobile], [data-reminder-consent]')) return;
    if (event.target.matches('[data-reminder-consent]') && event.target.checked) {
      event.target.removeAttribute('aria-invalid');
      this.#error('');
    }
    this.#refresh();
    if (event.target.value === 'custom' && this.#pointerPreset) {
      this.#dialog?.querySelector('[data-reminder-field]')?.focus();
    }
    this.#pointerPreset = false;
  };

  #onCancelled = () => {
    // Native dialog dismissal (Escape or the backdrop) still has to return focus.
    this.#close();
  };

  #onSubmit = (event) => {
    const form = event.target.closest('[data-reminder-form]');
    if (!form || !this.contains(form)) return;

    event.preventDefault();
    void this.#save(this.#editing, this.#chosen());
  };

  get #dialog() {
    return this.querySelector('[data-reminder-dialog]');
  }

  get #signInDialog() {
    return this.querySelector('[data-reminder-signin-dialog]');
  }

  #openSignIn(cell) {
    const dialog = this.#signInDialog;
    if (!cell || !dialog) return;

    this.#editing = cell;
    const product = dialog.querySelector('[data-reminder-product]');
    if (product) product.textContent = cell.querySelector('[data-reminder-edit]')?.dataset.title ?? '';

    const link = dialog.querySelector('[data-reminder-signin]');
    if (link) {
      const back = new URL(pageUrl(), window.location.origin);
      back.searchParams.set(REMIND_PARAM, cell.dataset.wishlistItem ?? '');
      link.href = signInUrl(back.pathname + back.search);
    }

    dialog.showModal();
    link?.focus();
  }

  #open(cell) {
    const dialog = this.#dialog;
    if (!cell || !dialog) return;

    this.#editing = cell;
    const existing = this.#byKey.get(cell.dataset.wishlistItem);
    const wrapper = cell.querySelector('[data-reminder-edit]');

    const product = dialog.querySelector('[data-reminder-product]');
    if (product) product.textContent = wrapper?.dataset.title ?? '';

    const field = dialog.querySelector('[data-reminder-field]');
    if (field) {
      field.min = localInputValue(new Date(Date.now() + 60_000));
      field.value = existing ? localInputValue(new Date(existing.due_at)) : '';
    }

    for (const radio of dialog.querySelectorAll('[data-reminder-preset]')) {
      radio.checked = existing ? radio.value === 'custom' : radio.value === '1d';
    }

    // One dialog serves the grid: reset both fields. The address is fetched rather than rendered —
    // in the markup it is readable by any third-party script regardless of consent.
    const emailField = dialog.querySelector('[data-reminder-email]');
    if (emailField) {
      emailField.value = emailField.defaultValue;
      if (emailField.hasAttribute('data-account-email')) {
        void accountEmail().then((address) => {
          if (address && this.#editing === cell) {
            emailField.value = address;
            this.#refresh();
          }
        });
      }
    }

    const mobileField = dialog.querySelector('[data-reminder-mobile]');
    if (mobileField) mobileField.value = existing?.phone ?? mobileField.defaultValue;

    const consentWrap = dialog.querySelector('[data-reminder-consent-wrap]');
    const consentBox = dialog.querySelector('[data-reminder-consent]');
    if (consentWrap) consentWrap.hidden = this.#consented;
    if (consentBox) {
      consentBox.checked = false;
      consentBox.removeAttribute('aria-invalid');
    }

    this.#refresh();

    const remove = dialog.querySelector('[data-reminder-remove]');
    if (remove) remove.hidden = !existing;

    const save = dialog.querySelector('[data-reminder-save]');
    if (save) save.textContent = existing ? this.dataset.updateLabel : this.dataset.saveLabel;

    this.#error('');
    dialog.showModal();
    field?.focus();
  }

  #close() {
    const toggle = this.#editing?.querySelector('[data-reminder-edit]');

    for (const dialog of [this.#dialog, this.#signInDialog]) {
      if (dialog?.open) dialog.close();
    }
    this.#editing = null;
    toggle?.focus();
  }


  /** 9am local on the target day: a reminder should land in the morning, not at 02:13. */
  #presetDate(value) {
    const at = new Date();
    at.setHours(9, 0, 0, 0);

    if (value === '1d') at.setDate(at.getDate() + 1);
    else if (value === '1w') at.setDate(at.getDate() + 7);
    else if (value === '1m') at.setMonth(at.getMonth() + 1);

    // A preset that resolves into the past is pushed to the next day.
    if (at.getTime() <= Date.now()) at.setDate(at.getDate() + 1);
    return at;
  }

  #chosen() {
    const dialog = this.#dialog;
    const picked = dialog?.querySelector('[data-reminder-preset]:checked')?.value;
    if (picked === 'custom') return dialog?.querySelector('[data-reminder-field]')?.value ?? '';
    return picked ? localInputValue(this.#presetDate(picked)) : '';
  }

  /** Always show the resolved moment, so "In a week" is never a guess. */
  #refresh() {
    const dialog = this.#dialog;
    if (!dialog) return;

    const picked = dialog.querySelector('[data-reminder-preset]:checked')?.value;
    const custom = dialog.querySelector('[data-reminder-custom]');
    if (custom) custom.hidden = picked !== 'custom';

    const local = this.#chosen();
    const summary = dialog.querySelector('[data-reminder-summary]');
    if (!summary) return;

    const at = local ? new Date(local) : null;
    if (!at || !Number.isFinite(at.getTime())) {
      summary.textContent = '';
      return;
    }

    const emailEl = dialog.querySelector('[data-reminder-email]');
    const hasEmail = !!emailEl && (emailEl.hasAttribute('data-account-email') || !!emailEl.value.trim());
    const hasPhone = !!dialog.querySelector('[data-reminder-mobile]')?.value.trim();
    const consentBox = dialog.querySelector('[data-reminder-consent]');
    const awaitingConsent = !!consentBox && !this.#consented && !consentBox.checked;

    const template = awaitingConsent
      ? this.dataset.summaryPendingText
      : hasPhone
        ? hasEmail
          ? this.dataset.summaryBothText
          : this.dataset.summarySmsText
        : this.dataset.summaryText;

    summary.textContent = (template ?? '').replace('[when]', this.#format(at));
  }
  #error(message) {
    const node = this.#dialog?.querySelector('[data-reminder-error]');
    if (!node) return;
    node.textContent = message ?? '';
    node.hidden = !message;
  }

  async #save(cell, local) {
    if (!cell) return;
    // Without this, two submits both mint an id and the second is rejected as a duplicate —
    // reporting failure for a reminder that did save.
    if (this.#saving) return;

    const dialog = this.#dialog;

    const emailEl = dialog?.querySelector('[data-reminder-email]');
    const mobileEl = dialog?.querySelector('[data-reminder-mobile]');
    // The marker means the account has an address; the field may not have been filled yet.
    const typedEmail = emailEl?.hasAttribute('data-account-email') ? 'account' : (emailEl?.value ?? '').trim();
    const typedPhone = (mobileEl?.value ?? '').trim();

    // The field is empty by default, so a number here is an affirmative opt-in to SMS.
    let phone = null;
    if (typedPhone) {
      phone = normalizePhone(typedPhone);
      if (!phone) {
        this.#error(this.dataset.errPhone);
        mobileEl?.focus();
        return;
      }
    }

    if (!typedEmail && !phone) {
      this.#error(this.dataset.errDestination);
      emailEl?.focus();
      return;
    }

    if (!String(local ?? '').trim()) {
      // Empty date means "remove" only when a reminder exists; otherwise it is a validation error.
      if (this.#byKey.get(cell.dataset.wishlistItem)) return this.#remove(cell);
      this.#error(this.dataset.errDate);
      this.#dialog?.querySelector('[data-reminder-preset][value="custom"]')?.focus();
      return;
    }

    const consentBox = dialog?.querySelector('[data-reminder-consent]');
    const consentGiven = consentBox?.checked === true;
    if (consentBox && !this.#consented && !consentGiven) {
      this.#error(this.dataset.errConsent);
      consentBox.setAttribute('aria-invalid', 'true');
      consentBox.focus();
      return;
    }
    consentBox?.removeAttribute('aria-invalid');

    const key = cell.dataset.wishlistItem ?? '';
    const [productId, variantId] = key.split(':');
    const handle = cell.querySelector('[data-reminder-edit]')?.dataset.handle ?? '';

    this.#saving = true;
    const saveButton = this.#dialog?.querySelector('[data-reminder-save]');
    saveButton?.setAttribute('aria-busy', 'true');
    saveButton?.setAttribute('aria-disabled', 'true');

    try {
      const saved = await saveReminder({
        productId,
        variantId,
        handle,
        local,
        listId: this.dataset.listId || undefined,
        // Not sent: the Worker resolves the destination from the account and ignores this.
        phone: phone ?? '',
        emailConsent: consentGiven,
      });
      if (!this.isConnected) return;

      this.#consented = true;
      this.#byKey.set(key, { ...saved, item_key: key });
      this.#render(cell, this.#byKey.get(key));
      this.#close();
      this.#say(this.dataset.savedText);

    } catch (error) {
      if (!this.isConnected) return;
      if (error?.code === 'consent_required') {
        this.#consented = false;
        const wrap = this.#dialog?.querySelector('[data-reminder-consent-wrap]');
        if (wrap) wrap.hidden = false;
        this.#refresh();
        const box = this.#dialog?.querySelector('[data-reminder-consent]');
        box?.scrollIntoView({ block: 'nearest' });
        box?.focus();
      }

      const codes = {
        too_many_reminders: this.dataset.fullText,
        consent_required: this.dataset.consentPromptText,
        consent_failed: this.dataset.consentFailedText,
      };
      const message = Object.hasOwn(codes, error?.code ?? '') ? codes[error.code] : this.dataset.errorText;
      this.#error(message);
      this.#say(message);
    } finally {
      this.#saving = false;
      saveButton?.removeAttribute('aria-busy');
      saveButton?.removeAttribute('aria-disabled');
    }
  }

  async #remove(cell) {
    const key = cell.dataset.wishlistItem ?? '';
    const existing = this.#byKey.get(key);
    if (!existing) return;
    // Same shape as #save: a second DELETE 404s and reports failure for a removal that succeeded.
    if (this.#saving) return;
    this.#saving = true;

    try {
      await deleteReminder(existing.id);
      if (!this.isConnected) return;

      this.#byKey.delete(key);
      this.#render(cell, null);
      this.#close();
      this.#say(this.dataset.removedText);
    } catch {
      if (this.isConnected) this.#say(this.dataset.errorText);
    } finally {
      this.#saving = false;
    }
  }
}

if (!customElements.get('wishlist-reminders')) {
  customElements.define('wishlist-reminders', WishlistReminders);
}
